import React, { useEffect, useState } from "react";
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import {
  apiDashboardCases,
  apiDashboardWidgets,
  getLoggedInUserData,
} from "../../helpers/backend_api";

const ApprentDashboard = () => {
  document.title = "Dashboard | ApprentAI";
  const user = getLoggedInUserData();

  const [widgets, setWidgets] = useState(null);
  const [cases, setCases] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState({ widgets: true, cases: true });
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    apiDashboardWidgets()
      .then((res) => {
        if (cancelled) return;
        setWidgets(res?.data || res);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Failed to load widgets");
      })
      .finally(() => {
        if (!cancelled) setLoading((l) => ({ ...l, widgets: false }));
      });

    apiDashboardCases({ limit: 10, offset: 0 })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data || res;
        const list = payload?.case_list || payload?.case_list_data || [];
        setCases({ rows: list, total: payload?.total_case || payload?.customer_case_count || 0 });
      })
      .catch((err) => {
        if (!cancelled) setError((e) => e || err?.message || "Failed to load cases");
      })
      .finally(() => {
        if (!cancelled) setLoading((l) => ({ ...l, cases: false }));
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Dashboard" pageTitle="ApprentAI" />

        <Row className="mb-3">
          <Col>
            <Alert color="info" className="mb-0">
              Welcome, <strong>{user?.name || "User"}</strong>
              {user?.role_data?.role_name ? ` (${user.role_data.role_name})` : ""}.
            </Alert>
          </Col>
        </Row>

        {error ? (
          <Row>
            <Col>
              <Alert color="danger">{String(error)}</Alert>
            </Col>
          </Row>
        ) : null}

        <Row>
          <Col md="6" lg="3">
            <Card>
              <CardBody>
                <div className="text-muted small">Cases assigned</div>
                <div className="fs-22 fw-semibold">
                  {loading.widgets ? <Spinner size="sm" /> : widgets?.widget1 ?? 0}
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md="6" lg="3">
            <Card>
              <CardBody>
                <div className="text-muted small">Total cases (latest fetch)</div>
                <div className="fs-22 fw-semibold">
                  {loading.cases ? <Spinner size="sm" /> : cases.total}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">Recent cases</h5>
              </CardHeader>
              <CardBody>
                {loading.cases ? (
                  <div className="text-center py-4">
                    <Spinner /> Loading…
                  </div>
                ) : (
                  <Table className="table-bordered align-middle table-nowrap mb-0">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name / Parties</th>
                        <th>Status</th>
                        <th>Lead Attorney</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cases.rows.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center text-muted">
                            No cases yet.
                          </td>
                        </tr>
                      ) : (
                        cases.rows.map((c) => (
                          <tr key={c.id || c.case_id}>
                            <td className="fw-medium">{c.id || c.case_id}</td>
                            <td>{c.case_name || c.nickname || c.case_parties || "—"}</td>
                            <td>{c.status_name || c.case_status?.status_name || "—"}</td>
                            <td>{c.lead_attorney || "—"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ApprentDashboard;
